// Reiter „Log“ der Lead-Seite (Runde 7B, L56): jede Änderung am Lead, an Kontakten, Follow-ups und Opportunities, neueste zuerst,
// nach Tagen gruppiert; eine Zeile aufklappen zeigt „vorher → nachher“ je Feld. Aus Close kommen die letzten 30 Tage (beim ersten
// Öffnen nachgeholt), danach hält das CRM den Verlauf selbst (`GET /api/leads/{id}/log`, API.md 22.2).
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { ApiFehler } from "../../api";
import { leadTiefeApi, type LeadLogAenderung, type LeadLogEintrag } from "../../api/leadTiefe";
import { useEchtzeit } from "../../api/echtzeit";
import { useMeta } from "../../daten/meta";
import { zeitpunktLang } from "../../werkzeuge/zeit";
import { Avatar, Laden } from "../Bausteine";
import { FehlerBox } from "../Fehler";
import { Icon, type IconName } from "../Icon";
import { kurzeZeit, tagKey, tagLabel } from "./feedHilfen";
import { TL } from "./leadTexte";
import "./leadTiefe.css";
import "./logReiter.css";

export interface LogFilter {
  /** lead | contact | task | opportunity | activity oder null = alles */
  gruppe: string | null;
  userId: string | null;
  von: string | null;
  bis: string | null;
}

const SYMBOL: Record<string, IconName> = {
  lead: "leads",
  status: "status",
  contact: "person",
  task: "aufgabe",
  opportunity: "opportunity",
  activity: "papierkorb",
};

const PERIODE: Record<string, string> = { one_time: "einmalig", monthly: "monatlich", annual: "jährlich" };
const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/;
const ISO_ZEIT = /^\d{4}-\d{2}-\d{2}T/;

/** Ein Wert aus dem Log lesbar: Ja/Nein, Beträge in Euro, Datum, „leer“ */
export function logWert(a: LeadLogAenderung, wert: unknown, waehrung: string): string {
  if (wert === null || wert === undefined || wert === "") return TL.log.leerWert;
  if (typeof wert === "boolean") return wert ? TL.log.ja : TL.log.nein;
  if (a.feld === "value" && typeof wert === "number") return new Intl.NumberFormat("de-DE", { style: "currency", currency: waehrung || "EUR" }).format(wert / 100);
  if (a.feld === "value_period" && typeof wert === "string") return PERIODE[wert] ?? wert;
  if (a.feld === "confidence" && typeof wert === "number") return `${wert} %`;
  if (typeof wert === "string" && ISO_ZEIT.test(wert)) return zeitpunktLang(wert);
  if (typeof wert === "string" && ISO_TAG.test(wert)) return wert.split("-").reverse().join(".");
  return String(wert);
}

function Zeile({ e, waehrung }: { e: LeadLogEintrag; waehrung: string }) {
  const [auf, setAuf] = useState(false);
  const gruppe = e.art.startsWith("status.") ? "status" : e.art.split(".")[0];
  const kannAuf = e.aenderungen.length > 0;
  return (
    <li className={`lg-zeile${auf ? " lg-zeile--auf" : ""}`} data-log={e.id}>
      <span className={`ls-symbol ls-symbol--status lg-symbol`} aria-hidden="true">
        <Icon name={SYMBOL[gruppe] ?? "info"} groesse={12} />
      </span>
      <div className="lg-zeile__inhalt">
        <div className="lg-zeile__kopf">
          {kannAuf ? (
            <button type="button" className="lg-zeile__titel lg-zeile__titel--knopf" aria-expanded={auf} onClick={() => setAuf((x) => !x)} title={auf ? TL.log.zuklappen : TL.log.aufklappen}>
              <Icon name={auf ? "chevron-unten" : "chevron-rechts"} groesse={11} />
              {e.titel}
            </button>
          ) : (
            <span className="lg-zeile__titel lg-zeile__titel--ohne">{e.titel}</span>
          )}
          <span className="lg-zeile__rechts">
            {e.nutzer_name ? (
              <span className="ls-eintrag__nutzer" title={e.nutzer_name}>
                <Avatar name={e.nutzer_name} id={e.nutzer_id} groesse={16} />
                <span className="ls-eintrag__nutzername">{e.nutzer_name}</span>
              </span>
            ) : null}
            <time className="ls-eintrag__zeit" dateTime={e.zeit} title={`${zeitpunktLang(e.zeit)} · ${e.quelle === "close" ? TL.log.close : TL.log.crm}`}>
              {kurzeZeit(e.zeit)}
            </time>
          </span>
        </div>
        {auf ? (
          <dl className="lg-aenderungen">
            {e.aenderungen.map((a, i) => (
              <div key={`${a.feld}-${i}`} className="lg-aenderungen__zeile">
                <dt>{a.label || a.feld}</dt>
                <dd>
                  <span className="lg-alt" title={TL.log.alt}>
                    {logWert(a, a.alt, waehrung)}
                  </span>
                  <Icon name="pfeil-rechts" groesse={11} />
                  <span className="lg-neu" title={TL.log.neu}>
                    {logWert(a, a.neu, waehrung)}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </li>
  );
}

export function LogReiter({ leadId, filter, onZahl }: { leadId: string; filter: LogFilter; onZahl?: (n: number | null) => void }) {
  const meta = useMeta();
  const waehrung = meta.meta?.org.currency ?? "EUR";
  const [eintraege, setEintraege] = useState<LeadLogEintrag[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hatMehr, setHatMehr] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const generation = useRef(0);
  const ende = useRef<HTMLDivElement>(null);
  const mehrRef = useRef<() => void>(() => undefined);

  const parameter = useCallback(
    () => ({ limit: 30, art: filter.gruppe ?? undefined, user_id: filter.userId ?? undefined, von: filter.von ? `${filter.von}T00:00:00` : undefined, bis: filter.bis ? `${filter.bis}T23:59:59` : undefined }),
    [filter.gruppe, filter.userId, filter.von, filter.bis],
  );

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setLaedt(true);
      setFehler(null);
      try {
        const r = await leadTiefeApi.log(leadId, parameter());
        if (gen !== generation.current) return;
        setEintraege(r.data);
        setCursor(r.cursor);
        setHatMehr(r.has_more);
        onZahl?.(r.total);
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leadId, parameter],
  );

  useEffect(() => {
    setEintraege([]);
    void laden();
  }, [laden]);

  // Ändert sich am Lead etwas, steht es nach kurzer Zeit im Log
  const zeitgeber = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(zeitgeber.current), []);
  useEchtzeit(["lead.updated", "task.created", "task.updated", "activity.deleted"], (e) => {
    if (e.lead_id !== leadId) return;
    window.clearTimeout(zeitgeber.current);
    zeitgeber.current = window.setTimeout(() => void laden(true), 1200);
  });

  const mehr = useCallback(async () => {
    if (!cursor || laedtMehr) return;
    setLaedtMehr(true);
    const gen = generation.current;
    try {
      const r = await leadTiefeApi.log(leadId, { ...parameter(), cursor });
      if (gen !== generation.current) return;
      setEintraege((alt) => {
        const bekannt = new Set(alt.map((x) => x.id));
        return [...alt, ...r.data.filter((x) => !bekannt.has(x.id))];
      });
      setCursor(r.cursor);
      setHatMehr(r.has_more);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedtMehr(false);
    }
  }, [cursor, laedtMehr, leadId, parameter]);
  mehrRef.current = () => void mehr();

  useEffect(() => {
    const el = ende.current;
    if (!el) return;
    const b = new IntersectionObserver((x) => x.some((i) => i.isIntersecting) && hatMehr && mehrRef.current(), { rootMargin: "300px" });
    b.observe(el);
    return () => b.disconnect();
  }, [hatMehr, eintraege.length]);

  const gefiltert = !!(filter.gruppe || filter.userId || filter.von || filter.bis);
  return (
    <div className="lg" aria-label={TL.log.reiter}>
      {fehler ? <FehlerBox fehler={fehler instanceof ApiFehler ? fehler : fehler} onNochmal={() => void laden()} kompakt /> : null}
      {laedt && !eintraege.length ? <Laden text={TL.log.laedt} klein /> : null}
      {!laedt && !fehler && !eintraege.length ? <div className="ls-feed__leer">{gefiltert ? TL.log.leerGefiltert : TL.log.leer}</div> : null}
      <ol className="lg-liste">
        {eintraege.map((e, i) => {
          const tag = tagKey(e.zeit);
          const davor = i > 0 ? tagKey(eintraege[i - 1].zeit) : null;
          return (
            <Fragment key={e.id}>
              {tag && tag !== davor ? (
                <li className="ls-feed__tag" aria-label={tagLabel(tag)}>
                  <span>{tagLabel(tag)}</span>
                </li>
              ) : null}
              <Zeile e={e} waehrung={waehrung} />
            </Fragment>
          );
        })}
      </ol>
      <div ref={ende} className="ls-feed__ende">
        {hatMehr ? (
          <button type="button" className="knopf knopf--klein" disabled={laedtMehr} onClick={() => void mehr()}>
            {laedtMehr ? "Lädt …" : TL.log.mehr}
          </button>
        ) : eintraege.length > 0 ? (
          <span className="lg-hinweis">{TL.log.hinweis}</span>
        ) : null}
      </div>
    </div>
  );
}
